// Catalog Shell - Modal / Dialog Manager
// ============================================================================

import { createElement } from '../utils/dom';
import { createButton, type ButtonVariant } from '../utils/pane-ui';
import { icons } from '../utils/icons';
import type { IDisposable } from '../components/types';

// ============================================================================
// Types
// ============================================================================

export type ModalSize = 'narrow' | 'normal' | 'wide' | 'full';

export interface ModalOptions {
    /** CSS class added to the modal card element */
    className?: string;
    /** If true, clicking the overlay backdrop closes the modal. Default: true */
    dismissOnOverlayClick?: boolean;
    /** If true, pressing Escape closes the modal. Default: true */
    dismissOnEscape?: boolean;
    /** Width constraint. Default: 'normal' */
    size?: ModalSize;
    /** Called when the modal is closed by any means */
    onClose?: () => void;
    /**
     * What to focus one frame after the modal appears.
     *
     * By default the modal claims the first focusable element inside the card,
     * which is right for a dialog the caller only assembled — and wrong for
     * one whose caller knows which field the user came to fill: the default
     * fires a frame LATER and silently takes the focus back, and anything
     * typed before that frame lands in the wrong input. Name the element to
     * claim it, or pass `false` to leave the focus exactly where it is.
     *
     * A caller that focuses something inside the card itself (synchronously,
     * before that frame) keeps it either way — the auto-focus stands down
     * when the card already holds the focus.
     */
    initialFocus?: HTMLElement | false;
}

export interface ConfirmOptions {
    title: string;
    /** Message body. Supports plain text. For rich content, use show() instead. */
    message: string;
    confirmLabel?: string;
    cancelLabel?: string;
    confirmVariant?: ButtonVariant;
    cancelVariant?: ButtonVariant;
    /** If true, the modal cannot be dismissed by overlay click or Escape */
    requireExplicitChoice?: boolean;
    /**
     * Set to false to hide the X close icon in the corner. Default true —
     * the X is a no-answer escape that resolves the promise to null so
     * callers can distinguish "user ignored the question" from
     * "user clicked Cancel".
     */
    showCloseIcon?: boolean;
}

export interface PromptOptions {
    title: string;
    /** Optional explanatory body above the input. Plain text. */
    message?: string;
    /** Label rendered above the input. Defaults to a type-to-confirm hint when `mustEqual` is set. */
    label?: string;
    placeholder?: string;
    /** Value the input starts with (already validated, so confirm may start enabled). */
    initialValue?: string;
    /** Confirm stays disabled until the value is non-empty after trimming. */
    required?: boolean;
    /**
     * Type-to-confirm: confirm stays disabled until the trimmed value equals
     * this text exactly. The expected text is always shown (in the caller's
     * label/message when it mentions it, else in a generated hint).
     */
    mustEqual?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    confirmVariant?: ButtonVariant;
    /** If true, the modal cannot be dismissed by overlay click or Escape */
    requireExplicitChoice?: boolean;
    /** Set to false to hide the X close icon in the corner. Default true. */
    showCloseIcon?: boolean;
}

export interface ModalHandle {
    /** Close the modal programmatically */
    close(): void;
    /** The modal card element (for appending additional content after creation) */
    element: HTMLElement;
    /** Promise that resolves when the modal is closed */
    closed: Promise<void>;
}

// ============================================================================
// Internal
// ============================================================================

interface ModalEntry {
    overlay: HTMLElement;
    card: HTMLElement;
    options: ModalOptions;
    previousFocus: Element | null;
    keydownHandler: (e: KeyboardEvent) => void;
    resolve: () => void;
}

const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), ' +
    'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

// ============================================================================
// ModalManager
// ============================================================================

export class ModalManager implements IDisposable {
    private container: HTMLElement;
    private stack: ModalEntry[] = [];
    private disposed = false;

    constructor(parentContainer: HTMLElement) {
        this.container = createElement('div', {
            className: 'cs-modal-container',
        });
        parentContainer.appendChild(this.container);
    }

    /** Show a modal with arbitrary content. Returns a handle for programmatic control. */
    show(content: HTMLElement, options: ModalOptions = {}): ModalHandle {
        if (this.disposed) {
            const noop = () => {};
            return { close: noop, element: document.createElement('div'), closed: Promise.resolve() };
        }

        const {
            className,
            dismissOnOverlayClick = true,
            dismissOnEscape = true,
            size = 'normal',
            onClose,
            initialFocus,
        } = options;

        const previousFocus = document.activeElement;

        // Build overlay
        const overlay = createElement('div', {
            className: 'cs-modal-overlay',
            style: { zIndex: String(400 + this.stack.length) },
        });

        // Build card
        const cardClasses = ['cs-modal-card', `cs-modal-card--${size}`];
        if (className) cardClasses.push(className);

        const card = createElement('div', {
            className: cardClasses.join(' '),
            attributes: { tabindex: '-1' },
        });
        card.appendChild(content);

        overlay.appendChild(card);

        // Overlay click dismiss
        if (dismissOnOverlayClick) {
            overlay.addEventListener('mousedown', (e) => {
                if (e.target === overlay) {
                    this.closeEntry(entry);
                }
            });
        }

        // Keydown handler for Escape and focus trapping
        const keydownHandler = (e: KeyboardEvent) => {
            // Every open modal has a capture listener on the document. Only the
            // TOPMOST one may act: a confirm raised from inside a modal must be
            // what Escape dismisses, not the modal underneath it — and it must
            // stop the older listeners from firing for the same keystroke.
            if (this.stack[this.stack.length - 1] !== entry) return;
            if (e.key === 'Escape' && dismissOnEscape) {
                e.stopImmediatePropagation();
                this.closeEntry(entry);
                return;
            }
            if (e.key === 'Tab') {
                this.trapFocus(card, e);
            }
        };

        // Create entry
        let resolvePromise: () => void;
        const closed = new Promise<void>(r => { resolvePromise = r; });

        const entry: ModalEntry = {
            overlay,
            card,
            options: { ...options, onClose },
            previousFocus,
            keydownHandler,
            resolve: resolvePromise!,
        };

        this.stack.push(entry);
        this.container.appendChild(overlay);

        // Attach keydown listener to the document (captures Escape from anywhere)
        document.addEventListener('keydown', keydownHandler, true);

        // Focus, one frame on: what the caller named, or the first focusable
        // element, or the card itself — but never over the top of a focus the
        // caller has already put inside this card.
        if (initialFocus !== false) {
            requestAnimationFrame(() => {
                if (initialFocus) {
                    initialFocus.focus();
                    return;
                }
                const active = document.activeElement;
                if (active && active !== card && card.contains(active)) return;
                const firstFocusable = card.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
                if (firstFocusable) {
                    firstFocusable.focus();
                } else {
                    card.focus();
                }
            });
        }

        const handle: ModalHandle = {
            close: () => this.closeEntry(entry),
            element: card,
            closed,
        };

        return handle;
    }

    /** Show a confirmation dialog. Resolves to true (confirm) or false (cancel/dismiss/X). */
    confirm(options: ConfirmOptions): Promise<boolean> {
        const {
            title,
            message,
            confirmLabel = 'Confirm',
            cancelLabel = 'Cancel',
            confirmVariant = 'yes',
            cancelVariant = 'default',
            requireExplicitChoice = false,
            showCloseIcon = true,
        } = options;

        return new Promise<boolean>((resolve) => {
            let resolved = false;

            const content = createElement('div', { className: 'modal' });
            if (showCloseIcon) content.classList.add('modal-has-close');

            if (showCloseIcon) {
                const closeBtn = document.createElement('button');
                closeBtn.type = 'button';
                closeBtn.className = 'cs-modal-close';
                closeBtn.setAttribute('aria-label', 'Close');
                closeBtn.appendChild(icons.close());
                closeBtn.addEventListener('click', () => {
                    resolved = true;
                    resolve(false);
                    handle.close();
                });
                content.appendChild(closeBtn);
            }

            const titleEl = document.createElement('h3');
            titleEl.textContent = title;
            content.appendChild(titleEl);

            const messageEl = createElement('div', { className: 'modal-content' });
            const p = document.createElement('p');
            p.textContent = message;
            messageEl.appendChild(p);
            content.appendChild(messageEl);

            const buttons = createElement('div', { className: 'modal-buttons' });

            const cancelBtn = createButton({
                label: cancelLabel,
                variant: cancelVariant,
                onClick: () => {
                    resolved = true;
                    resolve(false);
                    handle.close();
                },
            });
            buttons.appendChild(cancelBtn);

            const confirmBtn = createButton({
                label: confirmLabel,
                variant: confirmVariant,
                onClick: () => {
                    resolved = true;
                    resolve(true);
                    handle.close();
                },
            });
            buttons.appendChild(confirmBtn);

            content.appendChild(buttons);

            const handle = this.show(content, {
                dismissOnOverlayClick: !requireExplicitChoice,
                dismissOnEscape: !requireExplicitChoice,
                size: 'normal',
                onClose: () => {
                    if (!resolved) {
                        resolve(false);
                    }
                },
            });
        });
    }

    /**
     * Show a one-input dialog. Resolves to the trimmed text on confirm, or
     * `null` on cancel / X / overlay / Escape — the sibling of `confirm`, not
     * an overload of it, because callers test a `boolean` there.
     *
     * The confirm button stays disabled until the value is valid: non-empty
     * after trimming when `required`, and exactly `mustEqual` when that is set
     * (type-to-confirm). Enter in the input submits a valid value.
     */
    prompt(options: PromptOptions): Promise<string | null> {
        const {
            title,
            message,
            label,
            placeholder,
            initialValue = '',
            required = false,
            mustEqual,
            confirmLabel = 'Confirm',
            cancelLabel = 'Cancel',
            confirmVariant = 'yes',
            requireExplicitChoice = false,
            showCloseIcon = true,
        } = options;

        // Type-to-confirm is unusable unless the expected text is on screen:
        // default the label to it, and add a hint when the caller's own label
        // and message never mention it.
        const effectiveLabel = label ?? (mustEqual !== undefined ? `Type ${mustEqual} to confirm` : undefined);
        const mentionsExpected =
            mustEqual === undefined ||
            (effectiveLabel?.includes(mustEqual) ?? false) ||
            (message?.includes(mustEqual) ?? false);

        return new Promise<string | null>((resolve) => {
            let resolved = false;
            const finish = (value: string | null): void => {
                if (resolved) return;
                resolved = true;
                resolve(value);
                handle.close();
            };

            const content = createElement('div', { className: 'modal cs-modal-prompt' });
            if (showCloseIcon) content.classList.add('modal-has-close');

            if (showCloseIcon) {
                const closeBtn = document.createElement('button');
                closeBtn.type = 'button';
                closeBtn.className = 'cs-modal-close';
                closeBtn.setAttribute('aria-label', 'Close');
                closeBtn.appendChild(icons.close());
                closeBtn.addEventListener('click', () => finish(null));
                content.appendChild(closeBtn);
            }

            const titleEl = document.createElement('h3');
            titleEl.textContent = title;
            content.appendChild(titleEl);

            if (message || !mentionsExpected) {
                const messageEl = createElement('div', { className: 'modal-content' });
                if (message) {
                    const p = document.createElement('p');
                    p.textContent = message;
                    messageEl.appendChild(p);
                }
                if (!mentionsExpected) {
                    const hint = createElement('p', { className: 'cs-modal-prompt-hint' });
                    hint.textContent = `Type ${mustEqual} to confirm.`;
                    messageEl.appendChild(hint);
                }
                content.appendChild(messageEl);
            }

            const field = createElement('div', { className: 'cs-modal-prompt-field' });
            const input = createElement('input', {
                className: 'cs-modal-prompt-input',
                attributes: { type: 'text' },
            }) as HTMLInputElement;
            if (effectiveLabel) {
                const labelEl = createElement('label', {
                    className: 'cs-modal-prompt-label',
                    textContent: effectiveLabel,
                });
                labelEl.htmlFor = input.id || (input.id = `cs-prompt-${Math.random().toString(36).slice(2, 9)}`);
                field.appendChild(labelEl);
            }
            if (placeholder) input.placeholder = placeholder;
            input.value = initialValue;
            field.appendChild(input);
            content.appendChild(field);

            const buttons = createElement('div', { className: 'modal-buttons' });
            buttons.appendChild(createButton({
                label: cancelLabel,
                variant: 'default',
                onClick: () => finish(null),
            }));

            const isValid = (): boolean => {
                const value = input.value.trim();
                if (required && value.length === 0) return false;
                if (mustEqual !== undefined && value !== mustEqual) return false;
                return true;
            };

            const confirmBtn = createButton({
                label: confirmLabel,
                variant: confirmVariant,
                onClick: () => { if (isValid()) finish(input.value.trim()); },
            });
            confirmBtn.disabled = !isValid();
            buttons.appendChild(confirmBtn);
            content.appendChild(buttons);

            input.addEventListener('input', () => { confirmBtn.disabled = !isValid(); });
            input.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' && isValid()) {
                    e.preventDefault();
                    finish(input.value.trim());
                }
            });

            const handle = this.show(content, {
                dismissOnOverlayClick: !requireExplicitChoice,
                dismissOnEscape: !requireExplicitChoice,
                size: 'normal',
                // The first focusable element is the X; the input is what the
                // user came here to fill.
                initialFocus: input,
                onClose: () => { if (!resolved) { resolved = true; resolve(null); } },
            });

            input.focus();
        });
    }

    /** Close the topmost modal */
    closeTop(): void {
        if (this.stack.length > 0) {
            this.closeEntry(this.stack[this.stack.length - 1]);
        }
    }

    /** Close all modals */
    closeAll(): void {
        for (const entry of [...this.stack].reverse()) {
            this.closeEntry(entry);
        }
    }

    /** Whether any modal is currently open */
    isActive(): boolean {
        return this.stack.length > 0;
    }

    dispose(): void {
        if (this.disposed) return;
        this.disposed = true;
        this.closeAll();
        this.container.remove();
    }

    // ========================================================================
    // Internal
    // ========================================================================

    private closeEntry(entry: ModalEntry): void {
        const idx = this.stack.indexOf(entry);
        if (idx === -1) return;

        // Remove keydown listener
        document.removeEventListener('keydown', entry.keydownHandler, true);

        // Animate out
        entry.overlay.classList.add('cs-modal-overlay--closing');
        entry.card.classList.add('cs-modal-card--closing');

        // `animationend` and the timeout fallback can both fire, so guard
        // the body to run exactly once — otherwise `onClose` (and `resolve`)
        // run twice, which only happens to be harmless when every caller's
        // onClose is idempotent.
        let cleanedUp = false;
        const cleanup = () => {
            if (cleanedUp) return;
            cleanedUp = true;
            entry.overlay.remove();

            // Restore focus
            if (entry.previousFocus && entry.previousFocus instanceof HTMLElement) {
                entry.previousFocus.focus();
            }

            entry.options.onClose?.();
            entry.resolve();
        };

        entry.card.addEventListener('animationend', cleanup, { once: true });
        // Fallback in case animationend doesn't fire
        setTimeout(cleanup, 200);

        this.stack.splice(idx, 1);
    }

    private trapFocus(container: HTMLElement, event: KeyboardEvent): void {
        const focusable = container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    }
}
